import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import {
  ChevronLeft, Users, FileText, CheckCircle, Clock,
  XCircle, Heart, MessageCircle, Layers, TrendingUp,
  Star, Shield, ArrowRight, BarChart3, X,
} from "lucide-react";
import api from "../../services/axiosConfig";
import "./styles.css";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [selectedTestimony, setSelectedTestimony] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingCsv, setUploadingCsv] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const [dashRes, pendRes] = await Promise.all([
        api.get("/api/admin/dashboard"),
        api.get("/api/admin/testimonies/pending?size=5"),
      ]);
      setDashboard(dashRes.data);
      setPending(pendRes.data.content || []);
    } catch (err) {
      console.error("Error fetching admin dashboard:", err);
      if (err.response?.status === 403) {
        alert("You don't have admin access.");
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };



  const handleQuickApprove = async (id) => {
    try {
      await api.post(`/api/admin/testimonies/${id}/approve`);
      setPending((prev) => prev.filter((t) => t.id !== id));
      setDashboard((prev) => prev ? {
        ...prev,
        pendingTestimonies: prev.pendingTestimonies - 1,
        approvedTestimonies: prev.approvedTestimonies + 1,
      } : prev);
    } catch (err) {
      alert("Failed to approve: " + (err.response?.data?.message || err.message));
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingCsv(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/api/admin/testimonies/bulk-upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      alert(`Success! ${res.data.count} testimonies uploaded.`);
      fetchDashboard();
    } catch (err) {
      alert("Upload failed: " + (err.response?.data?.message || err.message));
    } finally {
      setUploadingCsv(false);
      e.target.value = null; // reset input
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <div className="admin-spinner" />
          <p>Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  const stats = [
    { key: "users", icon: <Users size={20} />, value: dashboard?.totalUsers || 0, label: "Total Users", gradient: "linear-gradient(135deg, #667eea, #764ba2)" },
    { key: "testimonies", icon: <FileText size={20} />, value: dashboard?.totalTestimonies || 0, label: "Testimonies", gradient: "linear-gradient(135deg, #f093fb, #f5576c)" },
    { key: "pending", icon: <Clock size={20} />, value: dashboard?.pendingTestimonies || 0, label: "Pending", gradient: "linear-gradient(135deg, #fccb90, #d57eeb)" },
    { key: "approved", icon: <CheckCircle size={20} />, value: dashboard?.approvedTestimonies || 0, label: "Approved", gradient: "linear-gradient(135deg, #4facfe, #00f2fe)" },
    { key: "rejected", icon: <XCircle size={20} />, value: dashboard?.rejectedTestimonies || 0, label: "Rejected", gradient: "linear-gradient(135deg, #fa709a, #fee140)" },
    { key: "categories", icon: <Layers size={20} />, value: dashboard?.totalCategories || 0, label: "Categories", gradient: "linear-gradient(135deg, #a18cd1, #fbc2eb)" },
    { key: "likes", icon: <Heart size={20} />, value: dashboard?.totalLikes || 0, label: "Total Likes", gradient: "linear-gradient(135deg, #ff9a9e, #fecfef)" },
    { key: "comments", icon: <MessageCircle size={20} />, value: dashboard?.totalComments || 0, label: "Total Comments", gradient: "linear-gradient(135deg, #84fab0, #8fd3f4)" },
  ];

  return (
    <div className="admin-page">

      {/* Top Bar */}
      <header className="admin-topbar">
        <button className="admin-back-btn" onClick={() => navigate("/profile")} aria-label="Back">
          <ChevronLeft size={20} />
        </button>
        <div className="admin-topbar-center">
          <Shield size={18} className="admin-shield-icon" />
          <h1>Admin Dashboard</h1>
        </div>
        <div className="admin-topbar-badge">ADMIN</div>
      </header>

      {/* Stats Grid */}
      <section className="admin-stats-grid">
        {stats.map((s, i) => (
          <div
            key={i}
            className="admin-stat-card"
            style={{ "--card-gradient": s.gradient }}
          >
            <div className="admin-stat-icon">{s.icon}</div>
            <div className="admin-stat-content">
              <p className="admin-stat-value">{s.value.toLocaleString()}</p>
              <p className="admin-stat-label">{s.label}</p>
            </div>
          </div>
        ))}
      </section>


      {/* Quick Actions */}
      <section className="admin-actions">
        <h2 className="admin-section-title">
          <BarChart3 size={16} /> Quick Actions
        </h2>
        <div className="admin-actions-grid">
          <button className="admin-action-card" onClick={() => navigate("/admin/moderation")}>
            <div className="admin-action-icon" style={{ background: "linear-gradient(135deg, #fccb90, #d57eeb)" }}>
              <Clock size={20} />
            </div>
            <div>
              <h3>Moderation Queue</h3>
              <p>{dashboard?.pendingTestimonies || 0} testimonies awaiting review</p>
            </div>
            <ArrowRight size={16} className="admin-action-arrow" />
          </button>

          <button className="admin-action-card" onClick={() => navigate("/admin/categories")}>
            <div className="admin-action-icon" style={{ background: "linear-gradient(135deg, #a18cd1, #fbc2eb)" }}>
              <Layers size={20} />
            </div>
            <div>
              <h3>Manage Categories</h3>
              <p>{dashboard?.totalCategories || 0} categories configured</p>
            </div>
            <ArrowRight size={16} className="admin-action-arrow" />
          </button>

          <button className="admin-action-card" onClick={() => navigate("/admin/users")}>
            <div className="admin-action-icon" style={{ background: "linear-gradient(135deg, #667eea, #764ba2)" }}>
              <Users size={20} />
            </div>
            <div>
              <h3>Manage Admins & Roles</h3>
              <p>{dashboard?.totalUsers || 0} users registered • Grant/Revoke Admin</p>
            </div>
            <ArrowRight size={16} className="admin-action-arrow" />
          </button>

          <label className="admin-action-card" style={{ cursor: "pointer", position: "relative" }}>
            <input 
              type="file" 
              accept=".csv" 
              style={{ position: "absolute", width: "100%", height: "100%", opacity: 0, cursor: "pointer" }} 
              onChange={handleCsvUpload} 
              disabled={uploadingCsv}
            />
            <div className="admin-action-icon" style={{ background: "linear-gradient(135deg, #4facfe, #00f2fe)" }}>
              <FileText size={20} />
            </div>
            <div>
              <h3>{uploadingCsv ? "Uploading..." : "Bulk Upload (CSV)"}</h3>
              <p>Upload testimonies for zones directly from spreadsheet</p>
            </div>
            <ArrowRight size={16} className="admin-action-arrow" />
          </label>
        </div>
      </section>

      {/* Recent Pending */}
      <section className="admin-recent">
        <div className="admin-section-header">
          <h2 className="admin-section-title">
            <TrendingUp size={16} /> Recent Pending Testimonies
          </h2>
          {pending.length > 0 && (
            <button className="admin-see-all" onClick={() => navigate("/admin/moderation")}>
              View all <ArrowRight size={14} />
            </button>
          )}
        </div>

        {pending.length === 0 ? (
          <div className="admin-empty">
            <Star size={28} />
            <p>No pending testimonies! All caught up. 🎉</p>
          </div>
        ) : (
          <div className="admin-pending-list">
            {pending.map((t) => (
              <div key={t.id} className="admin-pending-card">
                <div className="admin-pending-accent" />
                <div className="admin-pending-body">
                  <div className="admin-pending-top">
                    <span className="admin-pending-cat">{t.category?.name || "General"}</span>
                    <span className="admin-pending-date">
                      {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : ""}
                    </span>
                  </div>
                  <h3 className="admin-pending-title">{t.title}</h3>
                  <p className="admin-pending-author">By {t.user?.name || "Anonymous"} · {t.country || ""}</p>
                  <p className="admin-pending-excerpt">
                    {t.description ? t.description.slice(0, 120) + "..." : ""}
                  </p>
                  <div className="admin-pending-actions">
                    <button className="admin-approve-btn" onClick={() => handleQuickApprove(t.id)}>
                      <CheckCircle size={14} /> Approve
                    </button>
                    <button
                      className="admin-review-btn"
                      onClick={() => setSelectedTestimony(t)}
                    >
                      Review →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Selected Testimony Details Modal */}
      {selectedTestimony && (
        <div className="admin-modal-overlay" onClick={() => setSelectedTestimony(null)}>
          <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2 className="admin-modal-title">Review Testimony</h2>
              <button className="admin-modal-close" onClick={() => setSelectedTestimony(null)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="admin-modal-body">
              <div className="admin-modal-info-grid">
                <div className="admin-modal-info-item">
                  <span>Title</span>
                  <p>{selectedTestimony.title}</p>
                </div>
                <div className="admin-modal-info-item">
                  <span>Category</span>
                  <p>{selectedTestimony.category?.name || "General"}</p>
                </div>
                <div className="admin-modal-info-item">
                  <span>Author</span>
                  <p>{selectedTestimony.user?.name || "Anonymous"} ({selectedTestimony.country || "N/A"})</p>
                </div>
                <div className="admin-modal-info-item">
                  <span>Created Date</span>
                  <p>{selectedTestimony.createdAt ? new Date(selectedTestimony.createdAt).toLocaleString() : ""}</p>
                </div>
              </div>
              
              <div className="admin-modal-description">
                <span>Description</span>
                <p className="admin-modal-desc-text">{selectedTestimony.description}</p>
              </div>
            </div>
            
            <div className="admin-modal-footer">
              <button className="admin-btn-secondary" onClick={() => setSelectedTestimony(null)}>
                Cancel
              </button>
              <button 
                className="admin-btn-approve" 
                onClick={() => { handleQuickApprove(selectedTestimony.id); setSelectedTestimony(null); }}
              >
                <CheckCircle size={16} /> Approve Testimony
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
